// src/components/admin/SpecialsManagement.jsx
import React, { useState, useEffect } from "react";
import api from "../../../services/httpService";
import "./Specialization.css";

const SpecialsManagement = () => {
  // Specials State
  const [specialItems, setSpecialItems] = useState([]);
  const [isSpecialsLoading, setIsSpecialsLoading] = useState(true);
  const [specialsMessage, setSpecialsMessage] = useState("");

  // Form State
  const [activeForm, setActiveForm] = useState(null); // 'add' | 'edit' | null
  const [formData, setFormData] = useState({
    id: "",
    title: "",
    description: "",
    imageKey: "",
    isActive: true,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchSpecialItems();
  }, []);

  // ==================== FETCH ====================
  const fetchSpecialItems = async () => {
    setIsSpecialsLoading(true);
    try {
      const res = await api.get("/sentinix/specials");
      if (res.data.success) {
        const raw = res.data.data;
        const allItems = Array.isArray(raw) ? raw : raw ? [raw] : [];
        const items = allItems.filter((item) => !item.isDeleted);
        setSpecialItems(items);
      }
    } catch (error) {
      console.error("Error fetching specials:", error);
    } finally {
      setIsSpecialsLoading(false);
    }
  };

  // ==================== IMAGE HANDLER ====================
  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // ==================== FORM HANDLERS ====================
  const openAddForm = () => {
    setActiveForm("add");
    setFormData({ id: "", title: "", description: "", imageKey: "", isActive: true });
    setImageFile(null);
    setImagePreview(null);
    setSpecialsMessage("");
  };

  const openEditForm = (item) => {
    setActiveForm("edit");
    const existingImage = item.imageKey || item.ImageKey || item.image_key || "";
    setFormData({
      id: item.id,
      title: item.title || "",
      description: item.description || "",
      imageKey: existingImage,
      isActive: item.isActive ?? true,
    });
    setImageFile(null);
    setImagePreview(existingImage || null);
    setSpecialsMessage("");
  };

  const closeForm = () => {
    setActiveForm(null);
    setFormData({ id: "", title: "", description: "", imageKey: "", isActive: true });
    setImageFile(null);
    setImagePreview(null);
    setSpecialsMessage("");
  };

  // ==================== SAVE ====================
  const handleSaveSpecial = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      setSpecialsMessage("Title and Description are required");
      setTimeout(() => setSpecialsMessage(""), 3000);
      return;
    }

    setIsSaving(true);
    setSpecialsMessage("");

    try {
      const formDataPayload = new FormData();
      formDataPayload.append("title", formData.title);
      formDataPayload.append("description", formData.description);
      formDataPayload.append("isActive", formData.isActive);

      if (imageFile) {
        formDataPayload.append("imageKey", imageFile);
      } else if (formData.imageKey) {
        formDataPayload.append("imageKey", formData.imageKey);
      }

      let res;
      if (activeForm === "edit" && formData.id) {
        res = await api.put(
          `/sentinix/specials/${formData.id}`,
          formDataPayload,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        res = await api.post(
          "/sentinix/specials",
          formDataPayload,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      if (res.data.success) {
        setSpecialsMessage(
          activeForm === "edit"
            ? "Special updated successfully!"
            : "Special created successfully!"
        );
        fetchSpecialItems();
        setTimeout(() => closeForm(), 1500);
      } else {
        setSpecialsMessage(res.data.message || "Failed to save.");
      }
    } catch (error) {
      console.error("Error saving special:", error);
      setSpecialsMessage(error.response?.data?.message || "Error saving data");
    } finally {
      setIsSaving(false);
      setTimeout(() => setSpecialsMessage(""), 3000);
    }
  };

  // ==================== DELETE ====================
  const handleDeleteSpecial = async (id) => {
    if (!window.confirm("Are you sure you want to delete this special?")) return;

    try {
      const res = await api.delete(`/sentinix/specials/${id}`);
      if (res.data.success) {
        setSpecialsMessage("Special deleted successfully");
        fetchSpecialItems();
      } else {
        setSpecialsMessage(res.data.message || "Failed to delete");
      }
    } catch (error) {
      console.error("Error deleting special:", error);
      setSpecialsMessage(error.response?.data?.message || "Error deleting special");
    } finally {
      setTimeout(() => setSpecialsMessage(""), 3000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // ==================== RENDER ====================
  return (
    <div className="specials-mgmt-container">
      {/* Page Header */}
      <div className="specials-page-header">
        <h1>Specials Management</h1>
        <p>Manage your special offerings and featured content</p>
      </div>

      {/* ==================== SPECIALS SECTION ==================== */}
      <div className="specials-card">
        <div className="specials-card-header">
          <div>
            <h2>Specials</h2>
            <p>Manage your individual special entries</p>
          </div>
          <button onClick={openAddForm} className="btn-add-special">
            + Add New Special
          </button>
        </div>

        <div className="specials-card-body">
          {specialsMessage && (
            <div
              className={`form-message ${
                specialsMessage.includes("success") ? "success" : "error"
              }`}
            >
              {specialsMessage}
            </div>
          )}

          {/* ==================== ADD / EDIT MODAL ==================== */}
          {activeForm && (
            <div className="modal-overlay" onClick={closeForm}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>
                    {activeForm === "add" ? "Add New Special" : "Edit Special"}
                  </h3>
                  <button className="modal-close" onClick={closeForm}>
                    ✕
                  </button>
                </div>

                <div className="modal-body">
                  {specialsMessage && (
                    <div
                      className={`form-message ${
                        specialsMessage.includes("success") ? "success" : "error"
                      }`}
                    >
                      {specialsMessage}
                    </div>
                  )}

                  {/* Title */}
                  <div className="form-group-special">
                    <label>
                      Title <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      placeholder="Enter special title"
                      className="form-input-special"
                    />
                  </div>

                  {/* Image */}
                  <div className="form-group-special">
                    <label>Image</label>
                    {(imagePreview || formData.imageKey) && (
                      <div className="image-preview-special">
                        <img
                          src={imagePreview || formData.imageKey}
                          alt="Special"
                        />
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="file-input-special"
                      />
                      {imageFile && (
                        <p className="image-note">
                          ✅ {imageFile.name} — will upload on Save
                        </p>
                      )}
                      {!imageFile && formData.imageKey && (
                        <p className="image-note">
                          ℹ️ No new image selected — existing image will be kept
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="form-group-special">
                    <label>
                      Description <span className="required">*</span>
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      rows={5}
                      placeholder="Enter special description..."
                      className="form-textarea-special"
                    />
                  </div>

                  {/* isActive Toggle */}
                  <div className="form-group-special">
                    <label>Status</label>
                    <div className="toggle-group">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={formData.isActive}
                          onChange={(e) =>
                            setFormData({ ...formData, isActive: e.target.checked })
                          }
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <span className="toggle-label">
                        {formData.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button onClick={closeForm} className="btn-close-modal">
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveSpecial}
                    disabled={isSaving}
                    className="btn-save-special"
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-small"></span> Saving...
                      </>
                    ) : activeForm === "edit" ? (
                      "💾 Update Special"
                    ) : (
                      "💾 Save Special"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ==================== SPECIALS GRID ==================== */}
          {isSpecialsLoading ? (
            <div className="loading-box">
              <div className="spinner"></div>
              <p>Loading specials...</p>
            </div>
          ) : specialItems.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">⭐</span>
              <p>No specials added yet</p>
              <button onClick={openAddForm} className="btn-add-special">
                + Add Your First Special
              </button>
            </div>
          ) : (
            <div className="specials-grid">
              {specialItems.map((item) => (
                <div key={item.id} className="special-item-card">
                  <div className="special-item-image">
                    {item.imageKey ? (
                      <img src={item.imageKey} alt={item.title} />
                    ) : (
                      <div className="no-image-placeholder">
                        <span>No Image</span>
                      </div>
                    )}
                  </div>

                  <div className="special-item-info">
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    <div className="special-item-footer">
                      <span className="special-item-date">
                        {formatDate(item.createdAt)}
                      </span>
                      <span
                        className={`status-badge ${
                          item.isActive ? "active" : "inactive"
                        }`}
                      >
                        {item.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>

                  <div className="special-item-actions">
                    <button
                      onClick={() => openEditForm(item)}
                      className="btn-edit-item"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeleteSpecial(item.id)}
                      className="btn-delete-item"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpecialsManagement;